import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, MessageCircleQuestion, BookOpenText } from 'lucide-react';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';
import { useLanguage } from '../../contexts/LanguageContext';

export default function MarketingSupportPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const FAQ = [
    { q: t('support.faq1.q'), a: t('support.faq1.a') },
    { q: t('support.faq2.q'), a: t('support.faq2.a') },
    { q: t('support.faq3.q'), a: t('support.faq3.a') },
    { q: t('support.faq4.q'), a: t('support.faq4.a') },
    { q: t('support.faq5.q'), a: t('support.faq5.a') },
    { q: t('support.faq6.q'), a: t('support.faq6.a') },
  ];

  return (
    <MarketingLayout>
      <section style={{ maxWidth: 920, margin: '0 auto', padding: '72px 22px 32px' }}>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ margin: 0, fontSize: 12, letterSpacing: 1.6, textTransform: 'uppercase', color: '#E53935', fontWeight: 700 }}
        >
          {t('support.eyebrow')}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.05 }}
          data-testid="marketing-support-title"
          style={{
            margin: '14px 0 0',
            fontFamily: "'Inria Serif', serif",
            fontSize: 'clamp(2.4rem, 5vw, 4.2rem)',
            lineHeight: 1.02,
            letterSpacing: '-0.02em',
            color: 'var(--mkt-text)',
          }}
        >
          {t('support.title')}
        </motion.h1>
        <p style={{ margin: '22px 0 0', fontFamily: "'Manrope', sans-serif", fontSize: 17, lineHeight: 1.6, color: 'var(--mkt-text-muted)' }}>
          {t('support.subtitle')}
        </p>
      </section>

      <section style={{ maxWidth: 920, margin: '0 auto', padding: '20px 22px 0' }}>
        <div
          style={{
            display: 'grid',
            gap: 16,
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          }}
        >
          {[
            { icon: MessageCircleQuestion, title: t('support.card1.title'), body: t('support.card1.body'), action: () => navigate('/contact'), cta: t('support.card1.cta') },
            { icon: Mail, title: t('support.card2.title'), body: t('support.card2.body'), action: () => { window.location.href = 'mailto:contact@allurexp.com'; }, cta: t('support.card2.cta') },
            { icon: BookOpenText, title: t('support.card3.title'), body: t('support.card3.body'), action: () => { const el = document.getElementById('marketing-support-faq'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, cta: t('support.card3.cta') },
          ].map((card, idx) => {
            const Icon = card.icon;
            return (
              <article
                key={idx}
                data-testid={`marketing-support-card-${idx}`}
                style={{
                  background: 'var(--mkt-card-bg)',
                  border: '1px solid var(--mkt-card-border)',
                  borderRadius: 24,
                  padding: '24px 22px',
                  boxShadow: 'var(--mkt-card-shadow)',
                }}
              >
                <div style={{ width: 44, height: 44, borderRadius: 14, background: 'var(--mkt-accent-red-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={20} color="#E53935" />
                </div>
                <h3 style={{ margin: '16px 0 6px', fontFamily: "'Inria Serif', serif", fontSize: 20, color: 'var(--mkt-text)' }}>{card.title}</h3>
                <p style={{ margin: '0 0 14px', fontFamily: "'Manrope', sans-serif", fontSize: 14, lineHeight: 1.55, color: 'var(--mkt-text-muted)' }}>{card.body}</p>
                <button
                  type="button"
                  onClick={card.action}
                  data-testid={`marketing-support-card-cta-${idx}`}
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--mkt-text)',
                    background: 'transparent',
                    border: '1px solid var(--mkt-cta-outline-border)',
                    borderRadius: 9999,
                    cursor: 'pointer',
                    padding: '10px 16px',
                  }}
                >
                  {card.cta}
                </button>
              </article>
            );
          })}
        </div>
      </section>

      <section id="marketing-support-faq" style={{ maxWidth: 920, margin: '0 auto', padding: '56px 22px 80px' }} data-testid="marketing-support-faq">
        <h2 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 'clamp(1.8rem, 3.2vw, 2.6rem)', color: 'var(--mkt-text)', letterSpacing: '-0.01em' }}>
          {t('support.faq.title')}
        </h2>
        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {FAQ.map((item, idx) => (
            <details
              key={idx}
              data-testid={`marketing-support-faq-item-${idx}`}
              style={{
                background: 'var(--mkt-card-bg)',
                border: '1px solid var(--mkt-card-border)',
                borderRadius: 18,
                padding: '20px 22px',
                boxShadow: 'var(--mkt-card-shadow)',
              }}
            >
              <summary style={{ cursor: 'pointer', fontFamily: "'Manrope', sans-serif", fontSize: 16, fontWeight: 700, color: 'var(--mkt-text)', listStyle: 'none' }}>
                {item.q}
              </summary>
              <p style={{ margin: '12px 0 0', fontFamily: "'Manrope', sans-serif", fontSize: 15, lineHeight: 1.65, color: 'var(--mkt-text-muted)' }}>
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>
    </MarketingLayout>
  );
}
